import React, { useState, useEffect, useMemo } from 'react';
import { History, X, ArrowRight, Calendar, DollarSign, User, Clock, CheckCircle, TrendingUp, Sparkles } from 'lucide-react';
import api from '../services/api';

interface EvolutionHistoryModalProps {
    isOpen: boolean;
    type: 'subscription' | 'seance';
    item: any | null;
    onClose: () => void;
}

const fmtDate = (d?: string) => {
    if (!d) return '-';
    try {
        return new Date(d).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch {
        return d;
    }
};

const fmtDateTime = (d?: string) => {
    if (!d) return '-';
    try {
        return new Date(d).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return d;
    }
};

const EvolutionHistoryModal: React.FC<EvolutionHistoryModalProps> = ({
    isOpen,
    type,
    item,
    onClose
}) => {
    const [history, setHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen || !item) {
            setHistory([]);
            return;
        }

        const fetchHistory = async () => {
            try {
                setLoading(true);
                const endpoint = type === 'subscription' 
                    ? `/subscriptions/${item.id}/prolongations`
                    : `/seances/${item.id}/prolongations`;
                const res = await api.get(endpoint);
                setHistory(res.data || []);
            } catch (err) {
                console.error("Erreur chargement historique prolongations:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [isOpen, item, type]);

    const isAbo = type === 'subscription';
    const clientName = `${item?.first_name || ''} ${item?.last_name || ''}`.trim() || 'Client';
    const planName = item?.forfait_name || item?.type_name || (isAbo ? 'Abonnement' : 'Séance');
    const periodicity = item?.forfait_periodicity || item?.periodicity || (isAbo ? 'mois' : 'séance');
    const currentEndDate = isAbo ? item?.end_date : item?.session_date;
    const currentTotal = Number(item?.amount_paid || item?.expected_price || item?.amount || 0);

    // Données initiales déduites de l'historique
    const { initialEndDate, initialTotal, totalAddedAmount, totalAddedQuantity } = useMemo(() => {
        if (!history || history.length === 0) {
            return {
                initialEndDate: currentEndDate,
                initialTotal: currentTotal,
                totalAddedAmount: 0,
                totalAddedQuantity: 0
            };
        }
        // history est ordonné DESC (le plus récent en premier)
        const oldestProlongation = history[history.length - 1];
        const initEnd = oldestProlongation.previous_end_date || currentEndDate;
        const initTotal = Number(oldestProlongation.previous_total_price || 0);
        const addedAmount = history.reduce((sum, h) => sum + Number(h.added_price || 0), 0);
        const addedQty = history.reduce((sum, h) => sum + Number(h.quantity_added || 0), 0);

        return {
            initialEndDate: initEnd,
            initialTotal: initTotal,
            totalAddedAmount: addedAmount,
            totalAddedQuantity: addedQty
        };
    }, [history, currentEndDate, currentTotal]);

    if (!isOpen || !item) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100 flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-2.5 text-blue-900">
                        <div className="p-2 bg-blue-100 text-blue-700 rounded-xl">
                            <History size={22} />
                        </div>
                        <div>
                            <h3 className="text-base font-black text-gray-900 leading-tight">
                                Historique des évolutions & prolongations
                            </h3>
                            <p className="text-xs text-blue-700 font-medium">
                                {clientName} — {planName}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-white/80 transition-colors cursor-pointer"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Résumé synthétique de l'évolution */}
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                        {/* 1. Niveau Initial */}
                        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-2xs">
                            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                1. Souscription Initiale
                            </span>
                            <div className="text-base font-black text-slate-800">
                                {initialTotal.toLocaleString()} FCFA
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">
                                Échéance : <span className="font-semibold text-gray-700">{fmtDate(initialEndDate)}</span>
                            </div>
                        </div>

                        {/* 2. Prolongations ajoutées */}
                        <div className="bg-emerald-50/80 p-3 rounded-xl border border-emerald-200 shadow-2xs">
                            <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider block mb-1 flex items-center justify-between">
                                <span>2. Prolongation(s)</span>
                                <span className="bg-emerald-200/80 text-emerald-900 px-1.5 py-0.2 rounded font-black text-[10px]">
                                    {history.length}
                                </span>
                            </span>
                            <div className="text-base font-black text-emerald-700">
                                +{totalAddedAmount.toLocaleString()} FCFA
                            </div>
                            <div className="text-[11px] text-emerald-800 mt-0.5">
                                Quantité ajoutée : <span className="font-semibold">+{totalAddedQuantity} {periodicity}</span>
                            </div>
                        </div>

                        {/* 3. Niveau Actuel atteint */}
                        <div className="bg-blue-50/80 p-3 rounded-xl border border-blue-200 shadow-2xs">
                            <span className="text-[11px] font-bold text-blue-700 uppercase tracking-wider block mb-1">
                                3. Niveau Actuel
                            </span>
                            <div className="text-base font-black text-blue-700">
                                {currentTotal.toLocaleString()} FCFA
                            </div>
                            <div className="text-[11px] text-blue-800 mt-0.5">
                                Échéance actuelle : <span className="font-semibold">{fmtDate(currentEndDate)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Explication contextuelle en prose pour l'utilisateur */}
                    {history.length > 0 && (
                        <div className="bg-emerald-50 border border-emerald-200/80 text-emerald-900 rounded-lg p-2.5 text-xs flex items-center gap-2">
                            <Sparkles size={16} className="text-emerald-600 shrink-0" />
                            <span>
                                La souscription initiale était de <strong>{initialTotal.toLocaleString()} FCFA</strong> (fin le {fmtDate(initialEndDate)}). Avec <strong>{history.length} prolongation(s)</strong> (+{totalAddedQuantity} {periodicity} pour +{totalAddedAmount.toLocaleString()} FCFA), elle a évolué jusqu'à <strong>{currentTotal.toLocaleString()} FCFA</strong> (nouvelle échéance le {fmtDate(currentEndDate)}).
                            </span>
                        </div>
                    )}
                </div>

                {/* Content / Timeline pas à pas */}
                <div className="p-6 overflow-y-auto space-y-6">
                    {loading ? (
                        <div className="py-12 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
                            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <span>Chargement de l'historique...</span>
                        </div>
                    ) : (
                        <div className="relative border-l-2 border-blue-200 ml-4 space-y-6">
                            {/* Évolutions / Prolongations récentes */}
                            {history.map((entry, index) => {
                                const stepNumber = history.length - index;
                                return (
                                    <div key={entry.id} className="relative pl-6">
                                        {/* Dot indicator */}
                                        <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-600 border-2 border-white shadow-xs flex items-center justify-center">
                                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                        </div>

                                        <div className="bg-white border border-emerald-200/80 rounded-xl p-4 shadow-xs space-y-3">
                                            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-black text-[11px] uppercase tracking-wider">
                                                        Prolongation #{stepNumber}
                                                    </span>
                                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                                        <Clock size={12} />
                                                        {fmtDateTime(entry.created_at)}
                                                    </span>
                                                </div>
                                                {entry.cashier_name && (
                                                    <span className="text-[11px] text-gray-500 italic">
                                                        Opérateur : <strong>{entry.cashier_name}</strong>
                                                    </span>
                                                )}
                                            </div>

                                            {/* Évolution "était à tel niveau -> a évolué à tel niveau" */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs bg-slate-50/80 p-3 rounded-lg border border-slate-100">
                                                <div>
                                                    <span className="text-gray-500 font-semibold block mb-1">
                                                        Niveau précédent :
                                                    </span>
                                                    <div className="space-y-0.5 text-gray-700">
                                                        <div>📅 Échéance : <strong className="text-gray-900">{fmtDate(entry.previous_end_date)}</strong></div>
                                                        <div>💰 Montant cumulé : <strong className="text-gray-900">{Number(entry.previous_total_price).toLocaleString()} FCFA</strong></div>
                                                    </div>
                                                </div>

                                                <div className="sm:border-l sm:border-slate-200 sm:pl-3">
                                                    <span className="text-emerald-700 font-bold flex items-center gap-1 mb-1">
                                                        <ArrowRight size={13} />
                                                        A évolué vers :
                                                    </span>
                                                    <div className="space-y-0.5 text-emerald-950">
                                                        <div>📅 Nouvelle échéance : <strong className="text-emerald-700">{fmtDate(entry.new_end_date)}</strong></div>
                                                        <div>💰 Nouveau total : <strong className="text-emerald-700">{Number(entry.new_total_price).toLocaleString()} FCFA</strong></div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Détail de l'ajout */}
                                            <div className="flex flex-wrap items-center justify-between text-xs pt-1 text-gray-600">
                                                <div className="flex items-center gap-1.5 font-medium">
                                                    <span>Quantité ajoutée :</span>
                                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 font-bold rounded">
                                                        +{entry.quantity_added} {periodicity}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-gray-800">
                                                        +{Number(entry.added_price).toLocaleString()} FCFA
                                                    </span>
                                                    <span className="text-[10px] uppercase font-bold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                                        {entry.payment_method || 'Espèces'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Point de départ initial */}
                            <div className="relative pl-6">
                                <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-600 border-2 border-white shadow-xs"></div>
                                <div className="bg-blue-50/50 border border-blue-200/60 rounded-xl p-4 text-xs space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-black text-[11px] uppercase tracking-wider">
                                            Souscription Initiale (Départ)
                                        </span>
                                        <span className="text-gray-500 text-[11px]">
                                            {fmtDateTime(item.created_at)}
                                        </span>
                                    </div>
                                    <div className="text-gray-700 space-y-1 pt-1">
                                        <div>Date de début : <strong>{fmtDate(item.start_date || item.session_date)}</strong></div>
                                        <div>
                                            Échéance de départ :{' '}
                                            <strong className="text-blue-900 font-bold">
                                                {fmtDate(initialEndDate)}
                                            </strong>
                                        </div>
                                        <div>
                                            Montant souscrit à l'origine :{' '}
                                            <strong className="text-blue-900 font-bold">
                                                {initialTotal.toLocaleString()} FCFA
                                            </strong>
                                            {' '}<span className="text-gray-500 text-[10px] uppercase">({item.payment_method || 'Espèces'})</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 border-t border-gray-100 flex justify-end bg-gray-50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="py-2 px-5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-bold text-xs transition-colors cursor-pointer"
                    >
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EvolutionHistoryModal;
